import React, { useEffect, useState } from "react";
import { useIntl, FormattedMessage } from "react-intl";
import { Link } from "react-router-dom";
import { useGetSignupConfigQuery } from "../../services/nestjs-base-api";
import { BrandLogoIcon } from '../BrandLogo';

interface ModernAuthLayoutProps {
    children: React.ReactNode;
    leftContent?: React.ReactNode;
}

const ModernAuthLayout: React.FC<ModernAuthLayoutProps> = ({ children, leftContent }) => {
    const intl = useIntl();
    const [isMobile, setIsMobile] = useState(false);

    // Fetch public config to check if legal acceptance is enabled
    const { data: signupConfig } = useGetSignupConfigQuery();
    const legalAcceptanceEnabled = signupConfig?.legalAcceptanceEnabled ?? false;

    useEffect(() => {
        const checkMobile = () => {
            setIsMobile(window.innerWidth < 768);
        };
        checkMobile();
        window.addEventListener('resize', checkMobile);
        return () => window.removeEventListener('resize', checkMobile);
    }, []);

    const styles = {
        container: {
            minHeight: '100vh',
            display: 'flex',
            flexDirection: isMobile ? 'column' as const : 'row' as const,
            fontFamily: '"Plus Jakarta Sans", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
        },
        leftSide: {
            flex: isMobile ? 'none' : 1,
            background: 'linear-gradient(135deg, #F59E0B 0%, #FBBF24 100%)',
            display: isMobile ? 'none' : 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: isMobile ? '2rem 1rem' : '3rem',
            color: 'white',
            position: 'relative' as const,
            overflow: 'hidden',
            minHeight: isMobile ? 'auto' : '100vh',
            // Without border-box the padding is ADDED to the 100vh, so every
            // page using this layout scrolled ~96px for nothing. There is no
            // global box-sizing reset in this app, so it has to be said here.
            boxSizing: 'border-box' as const,
        },
        leftContent: {
            zIndex: 2,
            textAlign: 'center' as const,
            maxWidth: '500px',
        },
        backgroundPattern: {
            position: 'absolute' as const,
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            opacity: 0.1,
            background: `
                radial-gradient(circle at 25% 25%, rgba(255,255,255,0.2) 0%, transparent 50%),
                radial-gradient(circle at 75% 75%, rgba(255,255,255,0.2) 0%, transparent 50%)
            `,
        },
        rightSide: {
            flex: 1,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: isMobile ? '1rem' : '2rem',
            backgroundColor: '#fafbfc',
            // On mobile a full-height mobileHeader sits ABOVE this pane, so a
            // 100vh right side guarantees the page overflows by exactly the
            // header's height on every auth screen. The container is already
            // 100vh and this is flex:1, so it fills without asking.
            minHeight: isMobile ? 'auto' : '100vh',
            boxSizing: 'border-box' as const,
        },
        formContainer: {
            width: '100%',
            maxWidth: isMobile ? '100%' : '400px',
            padding: isMobile ? '2rem 1.5rem' : '3rem 2rem',
            backgroundColor: 'white',
            borderRadius: isMobile ? '16px' : '24px',
            boxShadow: isMobile ? '0 10px 25px -5px rgba(0, 0, 0, 0.1)' : '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
            border: '1px solid rgba(0, 0, 0, 0.05)',
            margin: isMobile ? '0' : 'auto',
        },
        logo: {
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            marginBottom: isMobile ? '1.5rem' : '2rem',
            gap: isMobile ? '8px' : '12px',
        },
        logoIcon: {
            width: isMobile ? '40px' : '48px',
            height: isMobile ? '40px' : '48px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
        },
        logoText: {
            fontSize: isMobile ? '20px' : '24px',
            fontWeight: '700',
            color: '#1f2937',
            letterSpacing: '-0.025em',
        },
        mobileHeader: {
            background: 'linear-gradient(135deg, #F59E0B 0%, #FBBF24 100%)',
            padding: '2rem 1rem 1rem',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: 'white',
        },
        footer: {
            marginTop: '2rem',
            paddingTop: '1.5rem',
            borderTop: '1px solid #e5e7eb',
            textAlign: 'center' as const,
        },
        footerLinks: {
            display: 'flex',
            justifyContent: 'center',
            gap: '1.5rem',
            marginBottom: '0.75rem',
            flexWrap: 'wrap' as const,
        },
        footerLink: {
            color: '#6b7280',
            textDecoration: 'none',
            fontSize: '0.875rem',
            transition: 'color 0.2s',
        },
        footerCopyright: {
            color: '#9ca3af',
            fontSize: '0.75rem',
        }
    };

    // Shared with the concierge offer screen so the two cannot drift apart.
    const LogoIcon = BrandLogoIcon;

    const defaultLeftContent = (
        <div>
            <div style={styles.logo}>
                <div style={styles.logoIcon}>
                    <LogoIcon size={isMobile ? 40 : 48} />
                </div>
                <span style={{ fontSize: '32px', fontWeight: '700' }}>
                    <span style={{ color: 'white' }}>BrandScience</span>
                    <span style={{ color: 'rgba(255,255,255,0.6)' }}>.ai</span>
                </span>
            </div>
            <h1 style={{ fontSize: isMobile ? '2rem' : '3rem', fontWeight: '700', marginBottom: '1.5rem', lineHeight: '1.2' }}>
                {intl.formatMessage({ id: 'auth.layout.unlock' })}
            </h1>
            <p style={{ fontSize: isMobile ? '1rem' : '1.25rem', opacity: 0.9, lineHeight: '1.6' }}>
                {intl.formatMessage({ id: 'auth.layout.description' })}
            </p>
        </div>
    );

    return (
        <div style={styles.container}>
            {/* Mobile Header */}
            {isMobile && (
                <div style={styles.mobileHeader}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                        <LogoIcon size={40} />
                        <span style={{ fontSize: '24px', fontWeight: '700' }}>
                            <span style={{ color: 'white' }}>BrandScience</span>
                            <span style={{ color: 'rgba(255,255,255,0.6)' }}>.ai</span>
                        </span>
                    </div>
                </div>
            )}

            {/* Left Side - Branding (Hidden on Mobile) */}
            <div style={styles.leftSide}>
                <div style={styles.backgroundPattern}></div>
                <div style={styles.leftContent}>
                    {leftContent || defaultLeftContent}
                </div>
            </div>

            {/* Right Side - Form */}
            <div style={styles.rightSide}>
                <div style={styles.formContainer}>
                    {!isMobile && (
                        <div style={styles.logo}>
                            <div style={styles.logoIcon}>
                                <LogoIcon size={48} />
                            </div>
                            <span style={styles.logoText}>
                                BrandScience<span style={{ color: '#94A3B8' }}>.ai</span>
                            </span>
                        </div>
                    )}
                    {children}

                    {/* Footer */}
                    <div style={styles.footer}>
                        {legalAcceptanceEnabled && (
                            <div style={styles.footerLinks}>
                                <Link
                                    to="/privacy-policy"
                                    style={styles.footerLink}
                                    onMouseEnter={(e) => e.currentTarget.style.color = '#F59E0B'}
                                    onMouseLeave={(e) => e.currentTarget.style.color = '#6b7280'}
                                >
                                    {intl.formatMessage({ id: 'auth.footer.privacyPolicy' })}
                                </Link>
                                <Link
                                    to="/terms-of-service"
                                    style={styles.footerLink}
                                    onMouseEnter={(e) => e.currentTarget.style.color = '#F59E0B'}
                                    onMouseLeave={(e) => e.currentTarget.style.color = '#6b7280'}
                                >
                                    {intl.formatMessage({ id: 'auth.footer.termsOfService' })}
                                </Link>
                            </div>
                        )}
                        <div style={styles.footerCopyright}>
                            <FormattedMessage id="auth.footer.copyright" values={{ year: new Date().getFullYear(), syriant: (chunks: React.ReactNode) => <a href="https://syriant.com" target="_blank" rel="noopener noreferrer" style={{ color: 'inherit', textDecoration: 'underline' }}>{chunks}</a> }} />
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

// @ts-ignore
export default ModernAuthLayout;